import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import type { EngagementHeatmapData, DropOffPoint } from '../../types'

interface EngagementHeatmapProps {
  data: EngagementHeatmapData[]
  dropOffPoints?: DropOffPoint[]
  videoDuration?: number  // Reserved for future use (showing video length marker)
  height?: number
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function EngagementHeatmap({
  data,
  dropOffPoints = [],
  videoDuration: _videoDuration,
  height = 200,
}: EngagementHeatmapProps) {
  // _videoDuration is reserved for future enhancement (e.g., showing end marker)
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-slate-400"
        style={{ height }}
      >
        No engagement data available
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorRetention" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
        <XAxis
          dataKey="time"
          stroke="#64748b"
          fontSize={12}
          tickLine={false}
          tickFormatter={formatTime}
        />
        <YAxis
          stroke="#64748b"
          fontSize={12}
          tickLine={false}
          axisLine={false}
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '8px',
            color: '#f8fafc',
          }}
          formatter={(value: number, name: string) => {
            if (name === 'retention') return [`${value}%`, 'Retention']
            if (name === 'viewers') return [value, 'Viewers']
            return [value, name]
          }}
          labelFormatter={(label) => `Time: ${formatTime(label as number)}`}
        />
        {dropOffPoints.map((point, index) => (
          <ReferenceLine
            key={index}
            x={point.time}
            stroke="#ef4444"
            strokeDasharray="3 3"
            strokeOpacity={0.7}
          />
        ))}
        <Area
          type="monotone"
          dataKey="retention"
          stroke="#10b981"
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorRetention)"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
